/**
 * Map Component Utilities
 * Shared utilities for HomePageMap and EntityDetailMap components
 * Reduces duplication and ensures consistency
 */

/**
 * Get MapContainer style configuration
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @returns {Object} Style object for MapContainer
 */
export function getMapContainerStyle(isFullscreenMode, isImmersiveMode) {
  if (isImmersiveMode) {
    // Immersive: map renders inside an already-offset container (left: 64px/360px in ImmersiveHome/ImmersiveEntityDetail).
    // Use 100% so the Leaflet canvas fills only the visible area — prevents click/drag coordinate mismatch.
    return { height: '100%', minHeight: '100%', maxHeight: 'none', width: '100%', borderRadius: '0' }
  }
  if (isFullscreenMode) {
    return { height: '100vh', minHeight: '100vh', maxHeight: '100vh', width: '100%', borderRadius: '0' }
  }
  return {
    height: 'calc(100vh - 280px)',
    minHeight: '450px',
    maxHeight: '600px',
    width: '100%',
    borderRadius: '0.5rem'
  }
}

/**
 * Get container wrapper class names
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @returns {string} CSS classes for container
 */
export function getMapWrapperClasses(isFullscreenMode, isImmersiveMode) {
  if (isImmersiveMode) return "h-full w-full"
  if (isFullscreenMode) return "h-screen w-screen"
  return "container mx-auto px-2 sm:px-4 py-4 sm:py-8"
}

/**
 * Get inner container class names
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @returns {string} CSS classes for inner container
 */
export function getMapInnerClasses(isFullscreenMode, isImmersiveMode) {
  return isFullscreenMode || isImmersiveMode 
    ? "h-full" 
    : "max-w-6xl mx-auto"
}

/**
 * Get search controls container classes
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @returns {string} CSS classes for search controls container
 */
export function getSearchControlsClasses(isImmersiveMode) {
  return isImmersiveMode 
    ? "fixed top-0 left-0 bottom-0 z-[998]"
    : "bg-white rounded-t-lg sm:rounded-lg shadow p-3 sm:p-6 sm:mb-6"
}

/**
 * Get map content wrapper classes (for the interactive map section)
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @param {boolean} isSpotModeActive - Whether spot mode is active
 * @returns {string} CSS classes for map content wrapper
 */
export function getMapContentClasses(isFullscreenMode, isImmersiveMode, isSpotModeActive = false) {
  if (isFullscreenMode || isImmersiveMode) {
    return `relative h-full${isSpotModeActive ? ' spot-mode-active' : ''}`
  }
  return `relative bg-white rounded-b-lg sm:rounded-lg shadow p-2 sm:p-6 mb-3 sm:mb-6 ${!isSpotModeActive && !isFullscreenMode ? '-mt-2 sm:mt-0' : ''}${isSpotModeActive ? ' spot-mode-active' : ''}`
}

/**
 * Generate unique marker key
 * @param {string|number} entityId - Entity ID
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {string} prefix - Optional prefix (e.g., 'entity', 'normal')
 * @returns {string} Unique marker key
 */
export function generateMarkerKey(entityId, latitude, longitude, prefix = '') {
  const prefixPart = prefix ? `${prefix}-` : ''
  return `${prefixPart}${entityId}-${latitude}-${longitude}`
}

/**
 * Generate MapContainer key for force remounting
 * @param {string|number} identifier - Map identifier (e.g., entityId or 'home')
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @returns {string} Unique map container key
 */
export function generateMapContainerKey(identifier, isFullscreenMode, isImmersiveMode) {
  return `map-${identifier}-${isFullscreenMode}-${isImmersiveMode}`
}

/**
 * Check if search controls should be shown
 * @param {boolean} isSpotModeActive - Whether spot mode is active
 * @param {boolean} isFullscreenMode - Whether map is in fullscreen mode
 * @param {boolean} isImmersiveMode - Whether map is in immersive mode
 * @param {boolean} isEntityMode - Whether in entity mode (for EntityDetailMap)
 * @returns {boolean} Whether to show search controls
 */
export function shouldShowSearchControls(isSpotModeActive, isFullscreenMode, isImmersiveMode, isEntityMode = false) {
  if (isSpotModeActive || isFullscreenMode) {
    return false
  }
  return isImmersiveMode || !isEntityMode
}

/**
 * Default MapContainer props that are common to both maps
 * @returns {Object} Common MapContainer props
 */
export function getCommonMapContainerProps() {
  return {
    zoomControl: false,
    attributionControl: false
  }
}

/**
 * Get TileLayer URL
 * Uses standard OpenStreetMap raster tiles — no API key required.
 * Carto's basemaps.cartocdn.com CDN now requires a (paid-tier-gated) API key
 * for anonymous usage, so it can't be used without an account; OSM tiles are
 * muted via the .leaflet-tile-pane CSS filter to keep the same low-noise look.
 * @returns {string} Tile layer URL
 */
export function getTileLayerUrl() {
  return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
}
